// AR view cells for Invoicing — the project accountant's aging / lien tracker.
// Everything here writes to the same invoice record the Inv view reads, so edits show in both.
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, MessageSquare, Plus, Bell, BellRing, ChevronLeft, ChevronRight } from "lucide-react";
import { focusCell, moveFrom } from "@/lib/gridNav";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { addInvoiceARNote, updateInvoice } from "@/lib/api";
import { shortDate, money } from "@/lib/format";

export const AR_COLUMNS = [
  { key: "lien_watch", label: "", noSort: true, title: "Lien watch — within 30 days of the intent date" },
  { key: "invoice_number", label: "Invoice #" },
  { key: "invoice_date", label: "Date", right: true, title: "Invoice date" },
  { key: "customer_name", label: "Customer" },
  { key: "pm_short", label: "PM" },
  { key: "job_name", label: "Name", title: "Work order name" },
  { key: "amount", label: "Amount", right: true },
  { key: "balance", label: "Balance", right: true, title: "Amount less paid to date — red when a partial payment leaves a balance due" },
  { key: "retainage", label: "Retainage", right: true },
  { key: "invoice_age_days", label: "Age", right: true, title: "Days since the invoice date — amber 30–40, red past 40" },
  { key: "status", label: "Status" },
  { key: "ar_notes", label: "Notes", noSort: true },
  { key: "actions", label: "Actions", noSort: true, right: true, title: "Remind · auto reminders · record payment (Foundation sync status in the tooltip)" },
  { key: "lien_status", label: "Lien", title: "Lien status — the arrow shows or hides the lien date columns" },
  { key: "last_work_date", label: "Last day", right: true, title: "Last day labor and/or material provided for lien rights" },
  { key: "intent_due", label: "Intent Due", right: true, title: "File intent 10 days prior to lien." },
  { key: "lien_date", label: "Lien Date", right: true, title: "File lien 4 months after last day of work." },
  { key: "enforce_date", label: "Enforce", right: true, title: "Enforce within 60 days of the lien date." },
  { key: "completion_date", label: "Completion", right: true },
  { key: "pct_complete", label: "% Complete", right: true, title: "Billed to date vs. work order value — 100% only once the job is marked complete" },
  { key: "lien_actions", label: "Lien Actions", noSort: true, right: true, title: "Email the lien rights notice" },
];

export const LIEN_STATUS_LABEL = { "": "—", out_of_window: "Out of Window", filed: "Filed", extended: "Extended" };

// Typed grid entry: 111526 · 11152026 · 11/15/26 · 11/15 (this year) → YYYY-MM-DD, or null.
export const parseTypedDate = (raw) => {
  const s = (raw || "").trim();
  const yr = new Date().getFullYear();
  let m, d, y;
  if (/[/\-.]/.test(s)) [m, d, y] = s.split(/[/\-.]/).map((x) => x.trim());
  else if (/^\d+$/.test(s) && [4, 6, 8].includes(s.length)) [m, d, y] = [s.slice(0, 2), s.slice(2, 4), s.slice(4) || undefined];
  else return null;
  y = y ? (y.length === 2 ? 2000 + +y : +y) : yr;
  const dt = new Date(y, +m - 1, +d);
  if (!m || !d || isNaN(dt) || dt.getMonth() !== +m - 1 || dt.getDate() !== +d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// Plain-text date that opens the native calendar on click; `auto` shows what the system derived when overridden.
// In the grid, typing on the cell replaces the whole date (never a partial edit); Delete clears it.
export function InlineDate({ value, onChange, title, testId, tone = "text-slate-600", auto }) {
  const ref = useRef(null);
  const root = useRef(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    const el = root.current;
    const onType = (e) => { if (e.detail === "Delete" || e.detail === "Backspace") { if (value) onChange(""); } else setDraft(e.detail); };
    el?.addEventListener("grid-type", onType);
    return () => el?.removeEventListener("grid-type", onType);
  }, [value, onChange]);
  const open = () => { const el = ref.current; if (!el) return; try { el.showPicker(); } catch { el.focus(); } };
  const finish = (save, move) => {
    const td = root.current?.closest("td");
    if (save) {
      const iso = parseTypedDate(draft);
      if (!iso) toast.error("Type a date like 111526 or 11/15/26");
      else if (iso !== value) onChange(iso);
    }
    setDraft(null);
    if (td) { focusCell(td); if (move) moveFrom(td, move, 0); }
  };
  const overridden = auto && value && value !== auto;
  return (
    <span ref={root} data-grid-type="date" className="relative inline-block whitespace-nowrap">
      {draft !== null ? (
        <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} data-grid-editing="1" data-testid={testId ? `${testId}-input` : undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "ArrowDown") { e.preventDefault(); finish(true, 1); }
            else if (e.key === "ArrowUp") { e.preventDefault(); finish(true, -1); }
            else if (e.key === "Tab") { e.preventDefault(); finish(true, 0); }
            else if (e.key === "Escape") { e.preventDefault(); finish(false, 0); }
          }}
          onBlur={() => setDraft(null)}
          className="h-6 w-20 rounded border border-brand bg-transparent px-1 text-right text-xs num outline-none" />
      ) : (
        <button type="button" onClick={open} data-testid={testId} data-grid-enter="1"
          title={[title, overridden ? `Auto: ${shortDate(auto)} — overridden` : null].filter(Boolean).join(" · ")}
          className={`text-xs num hover:underline ${tone} ${overridden ? "underline decoration-dotted" : ""}`}>
          {value ? shortDate(value) : "—"}
        </button>
      )}
      <input ref={ref} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} tabIndex={-1} aria-hidden="true"
        className="pointer-events-none absolute left-0 top-full h-0 w-0 opacity-0" />
    </span>
  );
}

export function LienFlag({ row, onClick }) {
  if (!row.lien_watch) return null;
  const d = row.lien_days_to_intent;
  const when = d == null ? "" : d > 0 ? `intent due in ${d} day${d === 1 ? "" : "s"}` : d === 0 ? "intent due today" : `intent was due ${-d} day${d === -1 ? "" : "s"} ago`;
  return (
    <button type="button" className="inline-flex hover:scale-110 transition-transform" onClick={onClick}
      title={`Lien watch — ${when} (${shortDate(row.intent_due)}); lien ${shortDate(row.lien_date)}${onClick ? " · click to show lien columns" : ""}`} data-testid={`lien-flag-${row.id}`}>
      <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Lien watch" />
    </button>
  );
}

// Header arrow that opens/closes a set of columns; blue while open so it reads as "click to close".
export function ColToggle({ open, onClick, label, testId }) {
  const Icon = open ? ChevronLeft : ChevronRight;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} title={open ? `Hide ${label}` : `Show ${label}`} aria-pressed={open} data-testid={testId}
      className="inline-flex h-5 w-5 items-center justify-center rounded text-brand transition-colors hover:text-brand-hover">
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

// "Lien" cell (right of Actions): status pill · filed badges. The watch flag stays up front by the invoice #.
export function LienCell({ row, onChanged, disabled, onOpen }) {
  return (
    <div className="flex items-center gap-1 whitespace-nowrap">
      <LienStatusSelect row={row} onChanged={onChanged} disabled={disabled} />
      <LienFiledBadges row={row} onOpen={onOpen} />
    </div>
  );
}

// Small pills logged from the Lien Filing Checklist; click opens the checklist.
export function LienFiledBadges({ row, onOpen }) {
  const items = [["intent", "Intent filed", row.intent_filed_on], ["lien", "Lien filed", row.lien_filed_on]].filter(([, , d]) => d);
  if (!items.length) return null;
  return items.map(([k, label, d]) => (
    <button key={k} type="button" onClick={onOpen ? () => onOpen(row) : undefined} disabled={!onOpen}
      title={`${label} ${shortDate(d)}${onOpen ? " — click to open the filing checklist" : ""}`} data-testid={`badge-${k}-filed-${row.id}`}
      className="inline-flex items-center whitespace-nowrap rounded-full border border-emerald-300 bg-emerald-50 px-1.5 py-px text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-default">
      {label}
    </button>
  ));
}

export function LienStatusSelect({ row, onChanged, disabled }) {
  const eff = row.lien_status_effective || "";
  const tone = eff === "filed" ? "text-emerald-700" : eff === "extended" ? "text-sky-700" : eff === "out_of_window" ? "text-slate-500" : "text-slate-600";
  const change = async (v) => {
    try { await updateInvoice(row.id, { lien_status: v }); onChanged?.(); }
    catch { toast.error("Could not update lien status"); }
  };
  const shown = eff === "out_of_window" && !row.lien_status ? "Out of Window" : (LIEN_STATUS_LABEL[row.lien_status || ""] || "—");
  return (
    <select value={row.lien_status || ""} onChange={(e) => change(e.target.value)} disabled={disabled} data-testid={`lien-status-${row.id}`}
      title={!row.lien_status && eff === "out_of_window" ? "Lien date passed without filing" : "Lien status — click or type O / F / E"}
      style={{ width: `${shown.length + 2.5}ch` }}
      className={`h-6 cursor-pointer appearance-none rounded border-0 bg-transparent px-1 text-[11px] shadow-none hover:bg-slate-100 focus:outline-none ${tone}`}>
      <option value="">{eff === "out_of_window" && !row.lien_status ? "Out of Window" : "—"}</option>
      <option value="out_of_window">Out of Window</option>
      <option value="filed">Filed</option>
      <option value="extended">Extended</option>
    </select>
  );
}

// Same look and feel as the Leads notes cell. Hover = read-only quick view; click = read + add.
export function InvoiceNotesCell({ row, onChanged, by = "", expanded = false }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const notes = row.ar_notes || [];
  const add = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    try { await addInvoiceARNote(row.id, text.trim(), by); setText(""); onChanged?.(); }
    catch { toast.error("Failed to add note"); }
    finally { setSaving(false); }
  };
  const list = (
    <div className="max-h-40 space-y-1 overflow-y-auto">
      {notes.length === 0 && <div className="py-2 text-center text-[10px] italic text-slate-300">No notes yet</div>}
      {[...notes].reverse().map((n) => (
        <div key={n.id || n.date} className="rounded bg-slate-50 px-2 py-1.5 text-xs" data-testid={`invoice-note-${row.id}-${n.id}`}>
          {n.date && <span className="text-[10px] font-medium text-slate-400">{shortDate(n.date)}</span>}
          {n.by && <span className="ml-1 text-[10px] text-slate-400">{n.by}</span>}
          <span className="ml-1.5 text-slate-700">{n.text}</span>
        </div>
      ))}
    </div>
  );
  const trigger = expanded ? (
    <button type="button" title="Click to read all or add a note" data-testid={`invoice-notes-${row.id}`} data-grid-enter="1"
      className="block w-[280px] rounded px-1 py-0.5 text-left text-xs leading-snug transition-colors hover:bg-slate-100">
      {notes.length === 0 ? <span className="inline-flex items-center gap-1 text-slate-400"><Plus className="h-3 w-3" />Add note</span>
        : [...notes].reverse().slice(0, 3).map((n) => (
          <div key={n.id || n.date} className="whitespace-normal break-words" data-testid={`invoice-note-inline-${row.id}-${n.id}`}>
            {n.date && <span className="text-[10px] font-medium text-slate-400 num">{shortDate(n.date)} </span>}
            <span className="text-slate-700">{n.text}</span>
          </div>
        ))}
      {notes.length > 3 && <div className="text-[10px] text-slate-400">+{notes.length - 3} more</div>}
    </button>
  ) : notes.length > 0 ? (
    <button type="button" title={`${notes.length} note${notes.length === 1 ? "" : "s"} — click to add`}
      className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
      data-testid={`invoice-notes-${row.id}`} data-grid-enter="1">
      <MessageSquare className="h-3.5 w-3.5" />
      <span className="num">{notes.length}</span>
    </button>
  ) : (
    <button type="button" title="Add the first note"
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
      data-testid={`invoice-notes-${row.id}`} data-grid-enter="1">
      <Plus className="h-4 w-4" />
    </button>
  );
  return (
    <HoverCard open={hover && !open && !expanded && notes.length > 0} onOpenChange={setHover} openDelay={150} closeDelay={80}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setHover(false); }}>
        <HoverCardTrigger asChild>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </HoverCardTrigger>
        <HoverCardContent className="w-72 p-3" align="center" data-testid={`invoice-notes-hover-${row.id}`}>
          <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold text-slate-500">
            <span>Notes</span><span className="font-normal text-slate-400">click to add</span>
          </div>
          {list}
        </HoverCardContent>
        <PopoverContent className="w-72 p-3" align="center" onClick={(e) => e.stopPropagation()}>
          <div className="mb-2 text-[11px] font-semibold text-slate-500">Notes</div>
          <div className="mb-2">{list}</div>
          <div className="flex gap-1">
            <Input className="h-7 flex-1 text-xs" placeholder="Add a note..." value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid={`invoice-note-input-${row.id}`} />
            <Button size="sm" className="h-7 px-2 text-xs" onClick={add} disabled={saving} data-testid={`invoice-note-add-${row.id}`}>Add</Button>
          </div>
        </PopoverContent>
      </Popover>
    </HoverCard>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CADENCE_LABEL = { daily: "daily", weekly: "weekly", days: "on chosen days" };

// Bell = automate the lien rights notice for this invoice until it is paid (sent by the 7 AM run).
// Ships OFF; the switch and every cadence pick save on their own — no Save button.
export function LienAutoBell({ row, onChanged, disabled }) {
  const saved = row.lien_auto_reminder || { enabled: false, cadence: "weekly", days: [] };
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(saved);
  const [saving, setSaving] = useState(false);
  const on = !!saved.enabled;
  const canAuto = !!row.lien_pay_by && row.balance > 0;
  const save = async (next) => {
    setCfg(next);
    if (next.enabled && next.cadence === "days" && !(next.days || []).length) return; // pick a day first
    setSaving(true);
    try { await updateInvoice(row.id, { lien_auto_reminder: { ...next, days: next.cadence === "days" ? (next.days || []) : [] } }); onChanged?.(); }
    catch { toast.error("Could not save the auto reminder"); setCfg(saved); }
    finally { setSaving(false); }
  };
  const title = on
    ? `Auto lien notice ON — ${CADENCE_LABEL[saved.cadence] || saved.cadence}${saved.cadence === "days" ? ` (${(saved.days || []).map((d) => WEEKDAYS[d]).join(", ")})` : ""}${row.lien_auto_last_sent ? ` · last auto send ${shortDate(row.lien_auto_last_sent)}` : ""}`
    : (canAuto ? "Automate the lien rights notice until paid (off by default)" : "Set the last day of work first — automation needs the lien dates");
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setCfg(saved); }}>
      <PopoverTrigger asChild>
        <button type="button" title={title} disabled={disabled || (!on && !canAuto)} data-testid={`btn-lien-auto-${row.id}`}
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-40 ${on ? "text-amber-600 hover:bg-amber-50" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}>
          {on ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-3" align="end" onClick={(e) => e.stopPropagation()} data-testid={`lien-auto-popover-${row.id}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-700">Auto lien notice</div>
            <div className="text-[10px] text-slate-400">Sends the lien rights notice each morning it is due, until paid</div>
          </div>
          <Switch checked={!!cfg.enabled} disabled={saving} onCheckedChange={(v) => save({ ...cfg, enabled: v })} data-testid={`lien-auto-switch-${row.id}`} />
        </div>
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Cadence</div>
          <div className="flex gap-1">
            {[["daily", "Daily"], ["weekly", "Weekly"], ["days", "Specific days"]].map(([v, l]) => (
              <button key={v} type="button" disabled={saving} onClick={() => save({ ...cfg, cadence: v })} data-testid={`lien-auto-cadence-${v}-${row.id}`}
                className={`rounded-full border px-2 py-0.5 text-[11px] ${cfg.cadence === v ? "border-brand bg-brand/10 text-brand font-semibold" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          {cfg.cadence === "days" && (
            <div className="flex gap-1 pt-1">
              {WEEKDAYS.map((d, i) => {
                const picked = (cfg.days || []).includes(i);
                return (
                  <button key={d} type="button" disabled={saving} data-testid={`lien-auto-day-${i}-${row.id}`}
                    onClick={() => save({ ...cfg, days: picked ? cfg.days.filter((x) => x !== i) : [...(cfg.days || []), i].sort() })}
                    className={`h-6 w-8 rounded border text-[10px] ${picked ? "border-brand bg-brand text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{d}</button>
                );
              })}
            </div>
          )}
          {cfg.enabled && cfg.cadence === "days" && !(cfg.days || []).length && (
            <div className="text-[10px] text-amber-700" data-testid={`lien-auto-pickday-${row.id}`}>Pick at least one day — nothing is saved until you do.</div>
          )}
        </div>
        <div className="text-[10px] text-slate-400 num">
          {row.lien_pay_by ? <>Pay by {shortDate(row.lien_pay_by)} · intent {shortDate(row.intent_due)}</> : "No lien dates yet"}
          {row.lien_auto_last_sent && <> · last auto send {shortDate(row.lien_auto_last_sent)}</>}
          {saving && <span className="ml-1 text-slate-500">· saving…</span>}
        </div>
        {!row.customer_email && (
          <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] text-rose-700" data-testid={`lien-auto-noemail-${row.id}`}>
            No customer email on this invoice — the auto notice has nowhere to go. Add a billing email on the invoice first.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export const fmtRetainage = (r) => (r.retainage > 0 ? `${money(r.retainage)} (${r.retainage_pct}%)` : "—");
